import { useMemo } from 'react';

import { appendShareId, generateShareIdSync } from '@change-corgi/core/longlinks';

import { getNormalizedLongUrl } from './api/getNormalizedLongUrl';
import type { ShortUrlInfo, ShortUrlState } from './context/types';
import { useShortUrlsLoader } from './useShortUrlsLoader';

function buildInfo(longUrl: string, cacheState: ShortUrlState | undefined): ShortUrlInfo {
	if (cacheState?.status === 'loaded') return cacheState.info;
	if (cacheState?.status === 'error') {
		return {
			url: appendShareId(cacheState.normalizedLongUrl, cacheState.localShareId),
			trackingProperties: {
				generated_share_id: cacheState.localShareId,
				generated_share_id_source: 'generated_after_failure',
			},
		};
	}
	if (cacheState?.status === 'loading') {
		return {
			url: appendShareId(cacheState.normalizedLongUrl, cacheState.localShareId),
			trackingProperties: {
				generated_share_id: cacheState.localShareId,
				generated_share_id_source: 'generated_before_response',
			},
		};
	}
	// First render: the URL hasn't been enqueued yet. Try the synchronous WASM
	// hash (available once the module is loaded, which is almost always before
	// any user interaction). Falls back to '' only if WASM isn't ready yet;
	// the effect in useShortUrlsLoader will enqueue the hash async in that case.
	const normalizedLongUrl = getNormalizedLongUrl(longUrl);
	const localShareId = longUrl ? generateShareIdSync(normalizedLongUrl) : null;
	if (localShareId !== null) {
		return {
			url: appendShareId(normalizedLongUrl, localShareId),
			trackingProperties: {
				generated_share_id: localShareId,
				generated_share_id_source: 'generated_before_response',
			},
		};
	}
	return {
		url: longUrl,
		trackingProperties: {
			generated_share_id: '',
			generated_share_id_source: 'generated_before_response',
		},
	};
}

// Tracking-aware variant of `useShortUrls`. Returns a `ShortUrlInfo` per key
// with a `share_id` once the local hash has been computed — consumers can merge
// `info.trackingProperties` straight into Amplitude payloads.
export function useShortUrlsInfo<T extends Record<string, string>>(
	urlsMapArg: T,
): Readonly<Record<keyof T, ShortUrlInfo>> {
	const { urlsMap, state } = useShortUrlsLoader(urlsMapArg);

	return useMemo(
		() =>
			// oxlint-disable-next-line typescript/no-unsafe-type-assertion
			Object.entries(urlsMap).reduce<Record<string, ShortUrlInfo>>((acc, [name, url]) => {
				// oxlint-disable-next-line no-param-reassign
				acc[name] = buildInfo(url, state.cache[url]);
				return acc;
			}, {}) as Record<keyof T, ShortUrlInfo>,
		[urlsMap, state],
	);
}
